import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Creature from "./Creature";
import usePointer from "./usePointer";
import useMediaQuery from "../../hooks/useMediaQuery";

const SIZE = 104;
const TAG = 32; // the name tag under each creature
const SPEED = 70; // pixels a second

/**
 * The lobby on the big screen: every player's creature bouncing around
 * the box with its name tag, like the old DVD screensaver. Positions live
 * in a ref and move by style each frame, so the page doesn't re-render
 * sixty times a second. With reduced motion they sit in rows instead.
 *
 * The loop finds each creature by its data-nickname every frame rather
 * than through ref callbacks: an inline ref callback is detached and
 * re-attached on every render, and doing the bookkeeping there threw
 * every creature to a new random spot on each poll (review of #451).
 * Motion is pruned against the players list instead.
 */
function BouncePen({ players }) {
  const boxRef = useRef(null);
  const motion = useRef(new Map());
  const still = useMediaQuery("(prefers-reduced-motion: reduce)");
  const look = usePointer();

  const names = players.map((p) => p.nickname).join("\n");
  useEffect(() => {
    const present = new Set(names.split("\n"));
    for (const name of motion.current.keys()) {
      if (!present.has(name)) motion.current.delete(name);
    }
  }, [names]);

  useEffect(() => {
    if (still) return undefined;
    let frame = 0;
    let last = performance.now();
    const step = (now) => {
      const box = boxRef.current;
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (box) {
        const width = box.clientWidth - SIZE;
        const height = box.clientHeight - SIZE - TAG;
        for (const node of box.querySelectorAll("[data-nickname]")) {
          const name = node.dataset.nickname;
          let m = motion.current.get(name);
          if (!m) {
            const angle = Math.random() * Math.PI * 2;
            m = {
              x: Math.random() * Math.max(0, width),
              y: Math.random() * Math.max(0, height),
              vx: Math.cos(angle) * SPEED,
              vy: Math.sin(angle) * SPEED,
            };
            motion.current.set(name, m);
          }
          m.x += m.vx * dt;
          m.y += m.vy * dt;
          if (m.x < 0 || m.x > width) {
            m.vx = -m.vx;
            m.x = Math.min(Math.max(m.x, 0), Math.max(0, width));
          }
          if (m.y < 0 || m.y > height) {
            m.vy = -m.vy;
            m.y = Math.min(Math.max(m.y, 0), Math.max(0, height));
          }
          node.style.transform = `translate(${m.x}px, ${m.y}px)`;
        }
      }
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [still]);

  const tag = (nickname) => (
    <span className="mt-1 max-w-40 truncate rounded-full border bg-card px-3 py-0.5 text-lg font-medium shadow-sm">
      {nickname}
    </span>
  );

  if (still) {
    return (
      <ul className="flex min-h-64 flex-wrap content-start gap-6 rounded-lg border bg-card p-6" aria-label="Players">
        {players.map((p) => (
          <li key={p.nickname} className="flex flex-col items-center">
            <Creature kind={p.creature} size={SIZE} look={look} />
            {tag(p.nickname)}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <ul
      ref={boxRef}
      className="relative h-[45vh] min-h-64 overflow-hidden rounded-lg border bg-card"
      aria-label="Players"
    >
      {players.map((p) => (
        <li
          key={p.nickname}
          data-nickname={p.nickname}
          className="absolute top-0 left-0 flex flex-col items-center will-change-transform"
          style={{ transform: "translate(-200px, -200px)" }}
        >
          <Creature kind={p.creature} size={SIZE} look={look} />
          {tag(p.nickname)}
        </li>
      ))}
    </ul>
  );
}

BouncePen.propTypes = {
  players: PropTypes.arrayOf(
    PropTypes.shape({ nickname: PropTypes.string.isRequired, creature: PropTypes.number.isRequired }),
  ).isRequired,
};

export default BouncePen;
